// Shared SEO helpers — enforce Ahrefs-friendly lengths and tag coverage.
// Title: <=60 chars. Description: 120-155 chars. Canonical + OG + Twitter on every page.

const SITE = 'MarineLink';
const BASE = 'https://marinelink.co.nz';
const DEFAULT_IMAGE = `${BASE}/logo.png`;

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/** Trim to <=60 chars on a word boundary. */
export function seoTitle(title: string): string {
  const t = title.replace(/\s+/g, ' ').trim();
  if (t.length <= 60) return t;
  const cut = t.slice(0, 57);
  return cut.slice(0, cut.lastIndexOf(' ')) + '...';
}

/** Normalize to 120-155 chars: pad short, trim long on word boundary. */
export function seoDescription(desc: string, padWith = ''): string {
  let d = desc.replace(/\s+/g, ' ').trim();
  if (d.length > 155) {
    const cut = d.slice(0, 152);
    d = cut.slice(0, cut.lastIndexOf(' ')) + '...';
  }
  if (d.length < 120 && padWith) {
    const pad = padWith.replace(/\s+/g, ' ').trim();
    d = `${d} ${pad}`.replace(/\s+/g, ' ').trim();
    if (d.length > 155) {
      const cut = d.slice(0, 152);
      d = cut.slice(0, cut.lastIndexOf(' ')) + '...';
    }
  }
  return d;
}

export interface SeoInput {
  title: string;
  description: string;
  /** Absolute path or full URL, e.g. /listing/foo/bar */
  path: string;
  image?: string;
  type?: string;
  keywords?: string;
  descriptionPad?: string;
}

/** Set title, description, canonical, Open Graph and Twitter card tags in one call. */
export function applySeo({ title, description, path, image, type = 'website', keywords, descriptionPad }: SeoInput) {
  const t = seoTitle(title);
  const d = seoDescription(description, descriptionPad);
  const url = path.startsWith('http') ? path : `${BASE}${path}`;
  const img = image || DEFAULT_IMAGE;

  document.title = t;
  upsertMeta('name', 'description', d);
  if (keywords) upsertMeta('name', 'keywords', keywords);
  upsertLink('canonical', url);

  upsertMeta('property', 'og:site_name', SITE);
  upsertMeta('property', 'og:title', t);
  upsertMeta('property', 'og:description', d);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:type', type);
  upsertMeta('property', 'og:image', img);
  upsertMeta('property', 'og:locale', 'en_NZ');

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', t);
  upsertMeta('name', 'twitter:description', d);
  upsertMeta('name', 'twitter:image', img);
}

/** Reset to site defaults (call on unmount). */
export function resetSeo() {
  applySeo({
    title: `${SITE} - NZ Marine Industry Directory`,
    description:
      "MarineLink Co NZ - the local directory for New Zealand's marine industry. Find boat charters, marine chandlers, boat rentals, services, marinas, Sealegs installers and more.",
    path: '/',
    keywords:
      'NZ marine directory, marine industry New Zealand, boat charters, chandlery, marinas, boat builders, Sealegs service, Sealegs installer, outboard repairs, marine electronics, boat transport, sailmakers, marine insurance',
  });
}
